import { useRef, useState, useEffect } from "react";
import { Pen, Check, RotateCcw, Trash2 } from "lucide-react";
import api from "../../lib/apiClient";
import { formatErr } from "../../lib/apiClient";

/**
 * Part 23 (Feb 2026) — Customer signature capture on the Job page.
 * Uses a plain HTMLCanvasElement with pointer events for cross-device drawing
 * (mouse + touch + stylus). Saved signatures render inline as an <img> from
 * the persisted data URL, so a page refresh shows the same signature.
 *
 * Persistence: POST /api/jobs/{id}/signature stores the data URL on the job.
 * DELETE /api/jobs/{id}/signature clears it.
 */
export function SignaturePad({ job, reload, setDlg }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const existing = job?.customer_signature;
  const [editingMode, setEditingMode] = useState(!existing);

  // Resize canvas to its container and set drawing style.
  useEffect(() => {
    if (!editingMode) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    const rect = parent.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = 200 * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = "200px";
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0F172A";
  }, [editingMode]);

  const pointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = pointerPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    const { x, y } = pointerPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasStrokes(true);
  };
  const stop = () => setDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
  };

  const save = async () => {
    if (!hasStrokes) { setErr("Please draw a signature first."); return; }
    setBusy(true); setErr("");
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await api.post(`/jobs/${job.id}/signature`, {
        image_data_url: dataUrl,
        signer_name: signerName,
      });
      setEditingMode(false);
      setHasStrokes(false);
      await reload();
    } catch (ex) { setErr(formatErr(ex)); }
    finally { setBusy(false); }
  };

  const requestClear = () => {
    setDlg?.({
      title: "Clear signature?",
      body: "The saved customer signature will be removed from this job. You can capture a new one after.",
      confirmLabel: "Clear",
      danger: true,
      onConfirm: async () => {
        setBusy(true);
        try {
          await api.delete(`/jobs/${job.id}/signature`);
          setEditingMode(true);
          await reload();
        } catch (ex) { setErr(formatErr(ex)); }
        finally { setBusy(false); }
      },
    });
  };

  return (
    <section
      data-testid="signature-section"
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md"
    >
      <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Pen size={15} className="text-[#0F4C81]" />
          <h3 className="font-display font-semibold text-slate-900 dark:text-slate-100">Customer signature</h3>
        </div>
        {existing && !editingMode && (
          <button
            onClick={() => setEditingMode(true)}
            data-testid="signature-recapture-btn"
            className="text-xs text-[#0F4C81] hover:underline"
          >
            Re-capture
          </button>
        )}
      </div>
      <div className="p-5">
        {existing && !editingMode ? (
          <div data-testid="signature-existing" className="space-y-3">
            <div className="border border-slate-200 dark:border-slate-700 rounded bg-white">
              <img src={existing.image_data_url} alt="Customer signature" className="max-h-40 mx-auto p-2" data-testid="signature-image" />
            </div>
            <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-400">
              <div>
                {existing.signer_name && <span className="font-semibold text-slate-800 dark:text-slate-200 mr-2">{existing.signer_name}</span>}
                <span className="text-slate-500">
                  Signed {existing.signed_at ? new Date(existing.signed_at).toLocaleString("en-CA", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
                </span>
              </div>
              <button
                onClick={requestClear}
                disabled={busy}
                data-testid="signature-clear-btn"
                className="text-xs text-rose-700 hover:underline flex items-center gap-1"
              >
                <Trash2 size={12}/> Clear
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="signature-editor">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Have the customer sign below to acknowledge the work.
            </p>
            <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-800 relative overflow-hidden">
              <canvas
                ref={canvasRef}
                data-testid="signature-canvas"
                className="touch-none w-full block cursor-crosshair"
                onPointerDown={start}
                onPointerMove={move}
                onPointerUp={stop}
                onPointerLeave={stop}
                onPointerCancel={stop}
              />
              {!hasStrokes && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-400 text-sm select-none">
                  Draw signature here
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Printed name (optional)"
                data-testid="signature-signer-name"
                className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-700 rounded text-sm bg-white dark:bg-slate-800"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={clearCanvas}
                  disabled={!hasStrokes || busy}
                  data-testid="signature-erase-btn"
                  className="px-3 py-2 text-xs font-medium border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1"
                >
                  <RotateCcw size={12}/> Erase
                </button>
                <button
                  onClick={save}
                  disabled={!hasStrokes || busy}
                  data-testid="signature-save-btn"
                  className="px-3 py-2 text-xs font-medium bg-[#0F4C81] hover:bg-[#0A365C] text-white rounded disabled:opacity-40 flex items-center gap-1"
                >
                  <Check size={12}/> Save signature
                </button>
              </div>
            </div>
            {err && <div data-testid="signature-error" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">{err}</div>}
          </div>
        )}
      </div>
    </section>
  );
}
